/**
 * FiasAIConversation - Message history display for the Fias AI panel/popup.
 *
 * Renders messages with markdown, inline proposal cards, auto-scroll,
 * and the Fias logo for assistant avatars.
 */

import { useEffect, useRef } from 'react';
import { User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { FiasAIProposal } from './FiasAIProposal';
import type { ArcheProposal } from './types';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  proposals?: ArcheProposal[];
  proposalStatus?: Record<string, 'applied' | 'rejected'>;
}

interface FiasAIConversationProps {
  archeId: string;
  messages: Message[];
  isStreaming: boolean;
  onApplyProposal?: (proposalId: string) => void;
  onRejectProposal?: (proposalId: string) => void;
}

export function FiasAIConversation({
  messages,
  isStreaming,
  onApplyProposal,
  onRejectProposal,
}: FiasAIConversationProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages or streaming updates
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, isStreaming]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <img src="/Heptagen_Logo_1.png" alt="Fias" className="h-8 w-8 mb-2" />
        <p className="text-sm text-muted-foreground">Ask Fias AI for help with this arche.</p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="p-3 space-y-3">
      {messages.map((msg) => (
        <div key={msg.id}>
          <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <img src="/Heptagen_Logo_1.png" alt="Fias" className="h-5 w-5" />
              </div>
            )}
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}
            >
              {msg.role === 'assistant' ? (
                <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              ) : (
                <span>{msg.content}</span>
              )}
              {msg.isStreaming && (
                <div className="flex items-center gap-1 mt-1">
                  <div className="w-1 h-1 rounded-full bg-primary/60 animate-bounce" />
                  <div className="w-1 h-1 rounded-full bg-primary/60 animate-bounce [animation-delay:150ms]" />
                  <div className="w-1 h-1 rounded-full bg-primary/60 animate-bounce [animation-delay:300ms]" />
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <User className="h-3 w-3 text-primary-foreground" />
              </div>
            )}
          </div>

          {/* Inline proposal cards */}
          {msg.proposals && msg.proposals.length > 0 && (
            <div className="ml-8 mt-2 space-y-2">
              {msg.proposals.map((proposal) => {
                const status = msg.proposalStatus?.[proposal.proposalId];
                if (status === 'rejected') return null;
                return (
                  <FiasAIProposal
                    key={proposal.proposalId}
                    proposal={proposal}
                    onApply={onApplyProposal || (() => {})}
                    onReject={onRejectProposal || (() => {})}
                    disabled={status === 'applied'}
                  />
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
